import React from "react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { FadeUp } from "../components/Motion";
import { Check, Download, Mail, Phone } from "lucide-react";
import SEO from "../components/SEO";

const credentials = [
  "Licensed Professional Engineer. Florida (since 2006), Virginia (since 1985), Puerto Rico (since 2023)",
  "Florida Certified General Contractor, CGC 1515086",
  "LEED Accredited Professional",
  "MS Civil Engineering, Stanford University",
  "MA Strategic Studies, U.S. Army War College",
  "BS General Engineering, U.S. Military Academy (West Point)",
  "Commander, USACE Jacksonville District (2004 to 2006)",
  "Commander, Heavy Construction Battalion, U.S. Army",
  "25 years active service, retired as Colonel, U.S. Army",
];

const domains = [
  "Construction Project Management and Standards of Care",
  "Federal Acquisition Regulation (FAR) and Federal Procurement",
  "FEMA Public Assistance Programs",
  "USACE Civil Works and Infrastructure",
  "Coastal and Water Resources Engineering",
  "Environmental Permitting and Compliance",
  "Disaster Response and Recovery Operations",
  "Scheduling and Delay Analysis",
  "Contract Administration and Disputes",
  "Construction Claims and Equitable Adjustments",
];

const highlights = [
  {
    period: "2004 to 2006",
    title: "USACE Jacksonville District Commander",
    body:
      "Commanded the largest USACE Civil Works District, overseeing approximately 980 personnel and administering an annual program of roughly $475 million across ports, waterways, coastal protection, flood control, and Everglades restoration. Led disaster response for Hurricanes Charley, Ivan, and Wilma.",
  },
  {
    period: "2006 to Present",
    title: "Founder, BCPeabody Construction Services",
    body:
      "Founded a federal general contractor and consulting firm. Directed more than 130 FEMA-funded infrastructure recovery and reconstruction projects in Puerto Rico. Extensive experience evaluating federal contract claims and equitable adjustments, supporting recovery of millions of dollars in contract modifications for clients.",
  },
  {
    period: "2001 to 2002",
    title: "Engineer Staff Officer, U.S. Central Command",
    body:
      "Led negotiations with the Government of Qatar for use and development of Al Udeid Air Base. Directed engineering planning and infrastructure design; coordinated the international agreement through DoD and the U.S. Department of State.",
  },
  {
    period: "Selected teaching",
    title: "Associate Professor of Civil Engineering, West Point",
    body:
      "Associate Professor of Civil Engineering, United States Military Academy at West Point. Primary Instructor for the USACE District and Division Commander Course.",
  },
  {
    period: "Public service",
    title: "Commissioner, Florida Century Commission",
    body: "Appointed by the Governor of Florida.",
  },
];

const engagements = [];

export default function ExpertWitness() {
  return (
    <>
      <SEO
        title="Construction Expert Witness | Col. Bob Carpenter, P.E."
        description="Expert witness for construction, USACE, FEMA and federal contract disputes. Former USACE Jacksonville District Commander. PE in FL, VA and PR."
        path="/expert-witness"
      />
      <Nav transparentOnTop />

      {/* Editorial Hero */}
      <section data-testid="ew-hero" className="relative min-h-screen bg-ink text-cream-light overflow-hidden grain">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-1/4 -right-1/4 w-[900px] h-[900px] rounded-full bg-copper/10 blur-[180px]" />
          <div className="absolute -bottom-1/4 -left-1/4 w-[700px] h-[700px] rounded-full bg-copper/5 blur-[160px]" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 pt-32 pb-16">
          <div className="grid grid-cols-12 gap-8 items-center">
            <div className="col-span-12 md:col-span-7">
              <div className="overline text-copper-light mb-8 flex items-center gap-3">
                <span className="w-12 h-px bg-copper" /> Expert Witness · Litigation Support
              </div>
              <h1 className="hero-headline text-5xl md:text-7xl lg:text-8xl mb-8">
                Construction experience<br />
                <span className="font-accent text-copper-light">that holds up under scrutiny.</span>
              </h1>
              <p className="text-cream-light/75 text-lg md:text-xl max-w-xl mb-8 leading-relaxed">
                Col. (Ret.) Robert M. Carpenter, P.E., provides independent analysis and expert testimony in construction, federal contracting, schedule, cost, and disaster recovery disputes.
              </p>
              <p className="text-cream-light/60 text-base max-w-xl mb-10 leading-relaxed">
                Available for deposition and trial testimony, expert reports, and case strategy. Career experience providing sworn statements and technical determinations as USACE District Commander and senior program executive.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact?type=expert-witness" data-testid="ew-cta-retain" className="btn-copper">
                  Retain for Consultation →
                </Link>
                <Link
                  to="/contact?type=expert-witness"
                  data-testid="ew-cv-on-request"
                  className="btn-ghost inline-flex items-center gap-2"
                >
                  CV available to counsel on request
                </Link>
              </div>
              <p className="text-cream-light/55 text-xs mt-6 uppercase tracking-[0.18em]">
                Initial consultations are confidential. Conflicts checked within one business day.
              </p>
            </div>

            {/* Direct attorney contact card */}
            <div className="col-span-12 md:col-span-4 md:col-start-9">
              <div className="border border-copper/30 p-8">
                <div className="overline text-copper-light mb-3">Direct Attorney Contact</div>
                <p className="text-cream-light/70 text-xs uppercase tracking-[0.16em] mb-5">
                  For counsel evaluating retention
                </p>
                <div className="space-y-4 text-sm text-cream-light/90 mb-8">
                  <a
                    href="mailto:bobcarpenter@bcpeabody.com"
                    data-testid="ew-email"
                    className="flex items-center gap-3 hover:text-copper-light transition-colors"
                  >
                    <Mail size={16} className="text-copper" />
                    bobcarpenter@bcpeabody.com
                  </a>
                  <a
                    href="tel:+18139195548"
                    data-testid="ew-phone"
                    className="flex items-center gap-3 hover:text-copper-light transition-colors"
                  >
                    <Phone size={16} className="text-copper" />
                    (813) 919-5548
                  </a>
                </div>
                <div className="border-t border-cream-light/12 pt-5">
                  <div className="overline text-copper-light mb-3">Available For</div>
                  <ul className="space-y-1.5 text-sm text-cream-light/85">
                    <li>Consulting and case strategy</li>
                    <li>Expert report preparation</li>
                    <li>Deposition testimony</li>
                    <li>Trial testimony</li>
                    <li>FEMA Public Assistance disputes</li>
                    <li>Federal acquisition and FAR matters</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Two-column legal brief layout */}
      <section id="qualifications" className="bg-cream-light py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-12">
          <aside className="col-span-12 md:col-span-3">
            <div className="md:sticky md:top-28">
              <div className="overline mb-4">Section</div>
              <nav className="flex flex-col gap-3 text-sm">
                <a href="#credentials" className="text-ink hover:text-copper">Credentials</a>
                <a href="#experience" className="text-ink hover:text-copper">Career Highlights</a>
                <a href="#expertise" className="text-ink hover:text-copper">Domains of Testimony</a>
                <a href="#approach" className="text-ink hover:text-copper">Approach</a>
              </nav>
            </div>
          </aside>

          <div className="col-span-12 md:col-span-9 space-y-20">
            <FadeUp>
              <div id="credentials">
                <div className="overline mb-4">Credentials</div>
                <h2 className="font-serif text-4xl md:text-5xl text-ink mb-10 tracking-tight">
                  A career of measurable authority.
                </h2>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
                  {credentials.map((c) => (
                    <li key={c} className="flex items-start gap-3 text-ink/80">
                      <Check className="text-copper shrink-0 mt-1" size={18} />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeUp>

            <FadeUp>
              <div id="experience">
                <div className="overline mb-4">Career Highlights</div>
                <h2 className="font-serif text-4xl md:text-5xl text-ink mb-10 tracking-tight">
                  Command, program, and craft.
                </h2>
                <div className="space-y-10">
                  {highlights.map((h) => (
                    <div key={h.title} className="grid grid-cols-12 gap-6 border-t border-ink/15 pt-8">
                      <div className="col-span-12 md:col-span-3 overline text-copper">{h.period}</div>
                      <div className="col-span-12 md:col-span-9">
                        <h3 className="font-serif text-2xl md:text-3xl text-ink mb-3 tracking-tight">
                          {h.title}
                        </h3>
                        <p className="text-ink/75 leading-relaxed">{h.body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </FadeUp>

            <FadeUp>
              <div id="expertise">
                <div className="overline mb-4">Domains of Testimony</div>
                <h2 className="font-serif text-4xl md:text-5xl text-ink mb-10 tracking-tight">
                  Areas of technical expertise.
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/10">
                  {domains.map((d, i) => (
                    <div key={d} className="bg-cream p-6 border-l-2 border-copper">
                      <div className="overline mb-2">{String(i + 1).padStart(2, "0")}</div>
                      <p className="text-ink font-medium">{d}</p>
                    </div>
                  ))}
                </div>
              </div>
            </FadeUp>

            <FadeUp>
              <div id="approach">
                <div className="overline mb-4">Approach</div>
                <h2 className="font-serif text-4xl md:text-5xl text-ink mb-8 tracking-tight">
                  Reports written to be defensible under cross.
                </h2>
                <p className="text-ink/80 text-lg leading-relaxed mb-6">
                  Every engagement begins with a conflicts check and a focused review of the technical questions in dispute. Opinions are grounded in the same standards Col. Carpenter used to sign federal quality determinations as USACE District Commander: documented, referenced to authority, and stress-tested against the field record.
                </p>
                <p className="text-ink/80 text-lg leading-relaxed">
                  His combination of operational command experience, formal engineering training at Stanford and West Point, and active Professional Engineer licensure in Florida, Virginia, and Puerto Rico positions him for cases that require both technical depth and federal program fluency.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-ink text-cream-light py-32 grain">
        <div className="max-w-[900px] mx-auto px-6 text-center">
          <div className="overline text-copper-light mb-6">Engagement Inquiries</div>
          <h2 className="font-serif text-5xl md:text-6xl mb-8 tracking-tight">
            Confidential. Direct. Responsive.
          </h2>
          <p className="text-cream-light/70 text-lg mb-10 max-w-xl mx-auto">
            For attorneys evaluating Col. Carpenter for retention, reach out directly or through our secure contact form. All initial inquiries are logged for conflict-of-interest checks before any case discussion.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="mailto:bobcarpenter@bcpeabody.com"
              data-testid="ew-cta-email"
              className="btn-copper"
            >
              Email Col. Carpenter →
            </a>
            <Link to="/contact?type=expert-witness" className="btn-ghost">Use Secure Form</Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
